import React, { useState, useEffect } from 'react';
import { Steps, Button, Icon, Card, Spin, message, Tooltip } from 'antd';
import { connect } from 'react-redux';
import { EaseModal } from '../../../libs/EaseModal';
import { TransferRequestForm } from '../Forms';
import ContactList from './ContactList';
import BeneficiaryList from './BeneficiaryList';
import { Text4, Text2 } from '../../../styles';
import { TransferRequest } from '../../../models';
import { alertify, handleApiExceptions } from '../../../app_methods';
import { OTPModal } from '../OTPModal';
import { MerchantService } from '../../../services';
import { updateCurrentUserAction } from '../../../redux/actions';
import './index.scss';


const transferRequest = new TransferRequest();

const TransferWizardComponent = (props) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isRequesting, setIsRequesting] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [selectedContact, setSelectedContact] = useState(props.selectedContact);
    const [selectedBeneficiary, setSelectedBeneficiary] = useState(props.selectedBeneficiary);
    const [showOTPModal, setShowOTPModal] = useState(false);
    const [isBankOTP, setIsBankOTP] = useState(false);
    const [otpRefKey, setOtpRefKey] = useState('');
    const virtual_accounts = props.current_user.virtual_accounts ?? []
    const [virtual_account, setVirtual_account] = useState(virtual_accounts.find((va) => va.virtual_account_number === transferRequest.virtual_account_number) || {})
    const [beneType, setBeneType] = useState("")

    useEffect(() => {
        let step = 0;
        if (props.selectedContact) {
            setSelectedContact(props.selectedContact);
            step = 1
            if (props.selectedBeneficiary) {
                setSelectedBeneficiary(props.selectedBeneficiary);
                step = 2
            }
        }
        setCurrentStep(step);
    }, [props]);

    const validateTransferRequest = async () => {
        setIsRequesting(true);
        let va = virtual_accounts.find((va) => va.virtual_account_number === transferRequest.virtual_account_number)
        if (!props.current_user.payment_modes.includes(transferRequest.payment_mode)) {
            handleApiExceptions(`${transferRequest.payment_mode} payment mode is currently disabled for your account.`);
            setIsRequesting(false);
            return;
        }
        const response = await transferRequest.validate();
        if (!response.success) {
            setIsRequesting(false);
            if (response.error_code == 'TR_E001') {
                alertify("Oops!!!", response.message, "confirm", { closable: true, onOk: () => generateTransferOTP("beneficiary", va), onCancel: () => { } });
            } else {
                handleApiExceptions(response)
            }
            return;
        }
        setIsRequesting(false);
        if (props?.current_user?.merchant_active_on_sbi) {
            generateTransferOTP("transfer", va);
            return;
        }
        if (props.current_user.bpo) {
            makeTransferRequest();
            return;
        }
        generateTransferOTP("transfer", va);
    };

    const generateTransferOTP = async (type = "transfer", virtual_accountParam) => {
        const va = virtual_accountParam || virtual_account;
        setIsRequesting(true);
        setBeneType(type);

        let queryData = {
            type,
            send_email: true,
            send_sms: true,
            amount: transferRequest.amount,
            payment_mode: transferRequest.payment_mode,
            beneficiary_name: selectedBeneficiary.account_name
        };

        if (props?.current_user?.merchant_active_on_sbi) {
            queryData.provider_code = va?.provider_code;
        }

        try {
            let response;
            if (props?.current_user?.merchant_active_on_sbi) {
                queryData.provider_code = queryData.provider_code || "SBI";
                response = await MerchantService.generate_bank_otp(queryData);
            } else {
                response = await MerchantService.generate_otp(queryData);
            }

            if (response.success) {
                setShowOTPModal(true);
                if (props?.current_user?.merchant_active_on_sbi) {
                    setIsBankOTP(true);
                    setOtpRefKey(response.otp_ref_key);
                }
            } else {
                handleApiExceptions(response.success ? "OTP generation failed." : response);
            }
        } catch (error) {
            handleApiExceptions({ success: false, status: error?.response?.status || 400 });
        } finally {
            setIsRequesting(false);
        }
    };

    const verifyOTP = (otp) => {
        setIsVerifying(true);
        if (!props?.current_user?.merchant_active_on_sbi) {
            MerchantService.verify_otp({ otp }).then((response) => {
                if (response.success) {
                    if (response.data.verified) {
                        setIsVerifying(false);
                        toggleOTPModal();
                        makeTransferRequest();
                    }
                    else {
                        setIsVerifying(false);
                        handleApiExceptions("OTP is incorrect.");
                    }
                }
                else {
                    setIsVerifying(false);
                    handleApiExceptions("OTP verification failed.");
                }
            }).catch((error) => {
                setIsVerifying(false);
                handleApiExceptions({ success: false, status: (error.response && error.response.status) || 400 });
            });
        } else {
            let otp_details = { 'otp': otp, 'otp_ref_key': otpRefKey }
            transferRequest.otp_details = otp_details
            setIsVerifying(false);
            toggleOTPModal();
            if (beneType == "transfer") {
                makeTransferRequest();
            }
            else {
                props.toggleTransferWizard();
                selectedBeneficiary["beneficiary_name"] = selectedBeneficiary?.account_name
                let data = {
                    account_number: selectedBeneficiary?.account_number,
                    ifsc: selectedBeneficiary?.account_ifsc,
                    beneficiary_name: selectedBeneficiary?.account_name,
                    id: selectedBeneficiary?.uuid,
                    otp_details: otp_details
                }
                props.addBeneficiaryOnBank(data);
            }
        }
    };

    const fetchMerchantInfo = () => {
        MerchantService.fetch().then((response) => {
            if (response.success) props.updateCurrentUser(response.data.merchant);
        });
    };

    const makeTransferRequest = async () => {
        setIsRequesting(true);
        const response = await transferRequest.initiate();
        if (!response.success) {
            setIsRequesting(false);
            handleApiExceptions(response);
            return;
        }
        alertify("Success", response.data.message, "success");
        props.toggleTransferWizard();
        fetchMerchantInfo();
        props.fetchBeneficiaries();
        setIsRequesting(false);
        transferRequest.reset();
    };

    const onStepChange = (action) => {
        if (action == "next") {
            switch (currentStep) {
                case 0:
                    if (!selectedContact) {
                        message.warning("Please select a Contact.");
                        return;
                    }
                    break;
                case 1:
                    if (!selectedBeneficiary) {
                        message.warning("Please select a Beneficiary.");
                        return;
                    }
                    break;
            }
            setCurrentStep(currentStep + 1);
        }
        else {
            setCurrentStep(currentStep - 1);
        }
    };

    const toggleOTPModal = () => {
        setShowOTPModal(!showOTPModal);
    };

    const handleContactSelection = (contact) => {
        setSelectedBeneficiary(null);
        setSelectedContact(contact);
    };

    const handleBeneficiarySelection = (beneficiary) => {
        setSelectedBeneficiary(beneficiary);
    };

    const onCancel = () => {
        props.toggleTransferWizard();
        setSelectedBeneficiary(props.selectedBeneficiary);
        setSelectedContact(props.selectedContact);
    };

    const renderTitle = () => {
        return (
            <div className="mb-4">
                <Text2 strong>Create Transfer Request</Text2>
            </div>
        )
    };

    const renderSteps = () => {
        return (
            <div className="mb-4">
                <Steps size="small" current={currentStep}>
                    <Steps.Step
                        title={<Text4>Select Contact</Text4>}
                        icon={<Icon type="user" />}
                        description={selectedContact ? <Text4 type="secondary">{selectedContact.name}</Text4> : null}
                    />
                    <Steps.Step
                        title={<Text4>Select Beneficiary</Text4>}
                        icon={<Icon type="bank" />}
                        description={selectedBeneficiary && selectedBeneficiary.beneficiary_type ?
                            <div>
                                {
                                    selectedBeneficiary.beneficiary_type == "bank_account" ?
                                        <Text4 type="secondary">{selectedBeneficiary.account_number}</Text4>
                                        :
                                        <Text4 type="secondary">{selectedBeneficiary.upi_handle}</Text4>
                                }
                            </div> : null
                        }
                    />
                    <Steps.Step
                        title={<Text4>Make Transfer</Text4>}
                        icon={<span dangerouslySetInnerHTML={{ __html: `&#8377; ` }} />}
                    />
                </Steps>
            </div>
        )
    };

    const renderStepContent = () => {
        let content = null;
        switch (currentStep) {
            case 0:
                content = <ContactList
                    selectedContact={selectedContact}
                    handleContactSelection={handleContactSelection}
                    setIsLoading={setIsLoading}
                />
                break;
            case 1:
                content = <BeneficiaryList
                    selectedContact={selectedContact}
                    selectedBeneficiary={selectedBeneficiary}
                    handleBeneficiarySelection={handleBeneficiarySelection}
                    setIsLoading={setIsLoading}
                />
                break;
            case 2:
                content = <TransferRequestForm
                    beneficiary={selectedBeneficiary}
                    loading={isRequesting}
                    onSubmit={(values) => {
                        transferRequest.set(values);
                        validateTransferRequest();
                    }}
                    generateTransferOTP={generateTransferOTP}
                />
                break;
            default:
                content = null;
                break;
        }
        return (
            <Card className="transfer-wizard-content">
                <Spin spinning={isLoading} tip="Please wait...">
                    {content}
                </Spin>
            </Card>
        )
    };

    return (
        <div>
            <EaseModal
                className="transfer-wizard-modal"
                width={850}
                title={false}
                visible={props.visible}
                onCancel={onCancel}
                centered
                hideFooter
                destroyOnClose
            >
                <div>
                    {renderTitle()}
                    {renderSteps()}
                    <div className="row align-items-center">
                        <div className="col-sm-1">
                            {
                                currentStep > 0 ? <Tooltip title="Go Back"><Button onClick={() => onStepChange("previous")} icon="left" shape="circle" /></Tooltip> : null
                            }
                        </div>
                        <div className="col-sm-10">
                            {renderStepContent()}
                        </div>
                        <div className="col-sm-1">
                            {
                                currentStep < 2 ? <Tooltip title="Next"><Button type="primary" onClick={() => onStepChange("next")} icon="right" shape="circle" /></Tooltip> : null
                            }
                        </div>
                    </div>
                </div>
            </EaseModal>
            <OTPModal
                visible={showOTPModal}
                onCancel={toggleOTPModal}
                verifyOTP={verifyOTP}
                loading={isVerifying}
                generateOTP={() => generateTransferOTP(beneType)}
                isBankOTP={isBankOTP}
            />
        </div>
    )
};

TransferWizardComponent.defaultProps = {
    visible: false,
    toggleTransferWizard: () => { },
    selectedContact: null,
    selectedBeneficiary: null,
    fetchBeneficiaries: () => { }
};

const mapStateToProps = state => ({
    current_user: state.userReducer.current_user
})

const mapDispatchToProps = dispatch => ({
    updateCurrentUser: user => dispatch(updateCurrentUserAction(user))
})

const TransferWizard = connect(mapStateToProps, mapDispatchToProps)(TransferWizardComponent);
export { TransferWizard };